// Libellés et états dérivés de l'application terrain : état local d'un objet d'après les opérations
// de la file (« Enregistré sur cet appareil », « Synchronisé », « Conflit »), libellés des types et
// des états du contrat, résumé lisible d'une charge pour l'écran des conflits, formats de date.
import type {
  ChargeRapportMission,
  EtatMission,
  EtatOrdreMission,
  EtatRealisation,
  OperationTerrain,
  PrioriteIntervention,
  RealisationTournee,
  SaisieConstat,
  TypeOperationTerrain,
  TypeRapportMission,
} from '@cc/contrats';
import type { OperationLocale } from './stockage';

type Operation = OperationTerrain;
type TypeRapport = TypeRapportMission;

export type EtatLocalObjet = 'conflit' | 'enregistre' | 'synchronise' | null;
export type TonLocal = 'neutre' | 'info' | 'attention' | 'ok';

export const LIBELLES_ETAT_LOCAL: Record<Exclude<EtatLocalObjet, null>, string> = {
  conflit: 'Conflit',
  enregistre: 'Enregistré sur cet appareil',
  synchronise: 'Synchronisé',
};

export const TONS_ETAT_LOCAL: Record<Exclude<EtatLocalObjet, null>, TonLocal> = { conflit: 'attention', enregistre: 'info', synchronise: 'ok' };

/** État local d'un objet : le conflit prime, puis l'attente, puis la synchronisation ; null sans opération. */
export function etatLocalObjet(operations: OperationLocale[], objetId: string): EtatLocalObjet {
  const siennes = operations.filter((o) => o.objet_id === objetId);
  if (siennes.length === 0) return null;
  if (siennes.some((o) => o.etat === 'conflit')) return 'conflit';
  if (siennes.some((o) => o.etat === 'en_attente' || o.etat === 'en_cours')) return 'enregistre';
  if (siennes.some((o) => o.etat === 'appliquee' || o.etat === 'ignoree_doublon')) return 'synchronise';
  return null;
}

export function etatLocalOperation(o: OperationLocale): EtatLocalObjet {
  if (o.etat === 'conflit') return 'conflit';
  if (o.etat === 'en_attente' || o.etat === 'en_cours') return 'enregistre';
  if (o.etat === 'appliquee' || o.etat === 'ignoree_doublon') return 'synchronise';
  return null;
}

export const LIBELLES_TYPE_RAPPORT: Record<TypeRapport, string> = { avant: 'Rapport avant travaux', apres: 'Rapport après travaux', final: 'Rapport final' };

export function libelleOperation(type: TypeOperationTerrain, charge: Operation['charge']): string {
  if (type === 'rapport_mission') return LIBELLES_TYPE_RAPPORT[(charge as ChargeRapportMission).type] ?? 'Rapport de mission';
  if (type === 'debut_mission') return 'Début de mission';
  if (type === 'realisation_tournee') return 'Réalisation de tournée';
  if (type === 'constat') return 'Constat';
  return 'Position';
}

export const LIBELLES_ETAT_MISSION: Record<EtatMission, string> = {
  affectee: 'Affectée',
  en_cours: 'En cours',
  rapport_recu: 'Rapport reçu',
  conflit: 'Conflit',
  validee: 'Validée',
  annulee: 'Annulée',
};

export const LIBELLES_PRIORITE: Record<PrioriteIntervention, string> = { basse: 'Priorité basse', normale: 'Priorité normale', haute: 'Priorité haute', urgente: 'Urgente' };

export const LIBELLES_ETAT_ORDRE: Record<EtatOrdreMission, string> = { emis: 'Émis', en_cours: 'En cours', constat_recu: 'Constat reçu', conflit: 'Conflit', clos: 'Clos', annule: 'Annulé' };

export const LIBELLES_REALISATION: Record<EtatRealisation, string> = { realisee: 'Réalisée', partielle: 'Réalisée en partie', non_realisee: 'Non réalisée' };

export const LIBELLES_TYPE_POINT: Record<'bac' | 'conteneur' | 'point_apport', string> = { bac: 'Bacs', conteneur: 'Conteneur', point_apport: "Point d'apport" };

export const LIBELLES_OBJET_ORDRE: Record<string, string> = {
  marche: 'Marché',
  emplacement: 'Emplacement',
  autorisation_domaine_public: 'Autorisation du domaine public',
  intervention: 'Intervention',
  lieu: 'Lieu',
};

/** Jours de la semaine ISO du contrat des tournées (1 = lundi ... 7 = dimanche). */
export const JOURS: Record<number, string> = { 1: 'lundi', 2: 'mardi', 3: 'mercredi', 4: 'jeudi', 5: 'vendredi', 6: 'samedi', 7: 'dimanche' };

const formatDateHeure = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const formatDate = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
const formatHeure = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });

export function dateHeure(iso: string | null | undefined): string {
  if (!iso) return 'jamais';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : formatDateHeure.format(d);
}

export function dateLongue(iso: string): string {
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? iso : formatDate.format(d);
}

export function heure(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : formatHeure.format(d);
}

/** Délai écoulé en mots (« il y a 3 min »), pour « dernier contact serveur ». */
export function depuis(iso: string | null, maintenant = new Date()): string {
  if (!iso) return 'jamais';
  const ms = maintenant.getTime() - new Date(iso).getTime();
  if (ms < 60_000) return "à l'instant";
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `il y a ${minutes} min`;
  const heures = Math.round(minutes / 60);
  if (heures < 24) return `il y a ${heures} h`;
  return `il y a ${Math.round(heures / 24)} j`;
}

/** Lignes lisibles d'une charge (écran des conflits : version de l'appareil). */
export function resumeCharge(o: Operation): string[] {
  if (o.type === 'rapport_mission') {
    const c = o.charge as ChargeRapportMission;
    const m = c.contenu?.materiaux ?? [];
    return [
      `Constats : ${c.contenu?.constats || 'aucun'}`,
      `Travaux : ${c.contenu?.travaux || 'aucun'}`,
      `Matériaux : ${m.length ? m.map((x) => (x.quantite !== undefined ? `${x.quantite} ${x.designation}` : x.designation)).join(', ') : 'aucun'}`,
      `Durée : ${c.contenu?.duree_minutes === undefined ? 'non renseignée' : `${c.contenu.duree_minutes} min`}`,
      `Photos : ${c.photos?.length ?? 0}`,
    ];
  }
  if (o.type === 'realisation_tournee') {
    const c = o.charge as RealisationTournee;
    return [`État : ${LIBELLES_REALISATION[c.etat]}`, `Points collectés : ${c.points_collectes.length}`, ...(c.motif ? [`Motif : ${c.motif}`] : []), ...(c.observations ? [`Observations : ${c.observations}`] : [])];
  }
  if (o.type === 'constat') {
    const c = o.charge as SaisieConstat;
    const nonConformes = c.reponses.filter((r) => r.reponse === 'non_conforme').length;
    return [
      `Conclusion : ${nonConformes > 0 ? 'non conforme' : 'conforme'}`,
      `Points non conformes : ${nonConformes} sur ${c.reponses.length}`,
      `Observations : ${c.observations || 'aucune'}`,
      ...(c.mesure_proposee ? [`Mesure corrective proposée : ${c.mesure_proposee.description} (${c.mesure_proposee.delai_jours} jours)`] : []),
    ];
  }
  if (o.type === 'debut_mission') return ['Début de mission enregistré'];
  return ['Position relevée'];
}
